export type EnumOption<T extends string = string> = {
  value: T;
  label: string;
};

export type ApplicationMaturity =
  | 'prototype'
  | 'pilot'
  | 'refinement'
  | 'production';

export type ApplicationEnvironment =
  | 'development'
  | 'test'
  | 'qa'
  | 'staging'
  | 'production';

export type ApplicationAccessType = 'hosted' | 'external' | 'concept' | 'local';

export type ApplicationDeploymentMethod = 'upload' | 'repository' | 'later';

export type ApplicationVisibility = 'private' | 'internal' | 'public';

export type ApplicationStatus = 'draft' | 'active' | 'archived';

export type ApplicationBuildStatus =
  | 'uploaded'
  | 'publishing'
  | 'published'
  | 'failed'
  | 'valid'
  | 'invalid';

export const applicationMaturityOptions: (EnumOption<ApplicationMaturity> & {
  description: string;
})[] = [
  {
    value: 'prototype',
    label: 'Prototype',
    description: 'Exploring ideas and validating concepts',
  },
  { value: 'pilot', label: 'Pilot', description: 'In pilot with real users' },
  {
    value: 'refinement',
    label: 'Refinement',
    description: 'Improving and hardening for production',
  },
  {
    value: 'production',
    label: 'Production',
    description: 'Live and delivering business value',
  },
];

export const applicationEnvironmentOptions: (EnumOption<ApplicationEnvironment> & {
  shortCode: string;
})[] = [
  { value: 'development', label: 'Development', shortCode: 'DEV' },
  { value: 'test', label: 'Test', shortCode: 'TEST' },
  { value: 'qa', label: 'QA', shortCode: 'QA' },
  { value: 'staging', label: 'Staging', shortCode: 'STAGING' },
  { value: 'production', label: 'Production', shortCode: 'PROD' },
];

export const applicationAccessTypeOptions: (EnumOption<ApplicationAccessType> & {
  description: string;
})[] = [
  {
    value: 'hosted',
    label: 'Host with Evolve AI',
    description:
      "We'll provide secure hosting, a URL and version management for your prototype.",
  },
  {
    value: 'external',
    label: 'Existing Web Application',
    description:
      'My application is already running somewhere else. I will provide the URL.',
  },
  {
    value: 'concept',
    label: 'Concept Only',
    description:
      "I'm registering an idea that isn't built yet. No deployment required.",
  },
  {
    value: 'local',
    label: 'Local Development',
    description:
      'The application is currently running on my machine. I will connect or upload it later.',
  },
];

export const applicationDeploymentMethodOptions: (EnumOption<ApplicationDeploymentMethod> & {
  description: string;
  available: boolean;
})[] = [
  {
    value: 'upload',
    label: 'Upload Build',
    description: 'Upload your built files (ZIP, static site, etc.)',
    available: true,
  },
  {
    value: 'repository',
    label: 'Connect repository',
    description:
      'Deploy automatically from GitHub, Azure DevOps, Bitbucket or GitLab.',
    available: false,
  },
  {
    value: 'later',
    label: 'Deploy Later',
    description:
      "I'll configure the deployment settings and files after registering this application.",
    available: true,
  },
];

export const applicationVisibilityOptions: (EnumOption<ApplicationVisibility> & {
  description: string;
})[] = [
  {
    value: 'private',
    label: 'Private',
    description: 'Only the owners can find and open it.',
  },
  {
    value: 'internal',
    label: 'Internal',
    description: 'Anyone signed in to Studio can find and open it.',
  },
  {
    value: 'public',
    label: 'Public',
    description: 'Anyone with the link can open it, no sign-in needed.',
  },
];

export const applicationStatusOptions: EnumOption<ApplicationStatus>[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
];

export const applicationBuildStatusOptions: EnumOption<ApplicationBuildStatus>[] =
  [
    { value: 'uploaded', label: 'Uploaded' },
    { value: 'publishing', label: 'Publishing' },
    { value: 'published', label: 'Published' },
    { value: 'failed', label: 'Failed' },
    { value: 'valid', label: 'Valid' },
    { value: 'invalid', label: 'Invalid' },
  ];

export const pendingBuildStatuses: ApplicationBuildStatus[] = [
  'uploaded',
  'publishing',
];

export const isPendingBuildStatus = (value: string | null | undefined) =>
  pendingBuildStatuses.includes(value as ApplicationBuildStatus);

export const optionLabel = (
  options: EnumOption[],
  value: string | null | undefined,
) => options.find((option) => option.value === value)?.label ?? value ?? '';

export const environmentShortCode = (value: string | null | undefined) =>
  applicationEnvironmentOptions.find((option) => option.value === value)
    ?.shortCode ??
  value?.toUpperCase() ??
  '';

export const nextMaturity = (value: string | null | undefined) => {
  const index = applicationMaturityOptions.findIndex(
    (option) => option.value === value,
  );
  if (index < 0) return undefined;
  return applicationMaturityOptions[index + 1];
};
